import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import Toggle from "@/app/components/ui/Toggles";
import Input from "@/app/components/ui/Inputs/Input";
import { useRouter } from "@/app/hooks/useRouter";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useStaffProfileCitiesAPI } from "@/app/hooks/api_hooks/configuration/useStaffProfileCitiesAPI";
import { useStaffProfileSaveAPI } from "@/app/hooks/api_hooks/configuration/useStaffProfileSaveAPI";
import { useStaffProfileDetailAPI } from "@/app/hooks/api_hooks/configuration/useStaffProfileDetailAPI";

const STAFF_TYPES = [
  { id: "groomer", title: "Groomer" },
  { id: "doctor", title: "Doctor" },
  { id: "paravet", title: "Paravet" },
  { id: "clinic_manager", title: "Clinic Manager" },
  { id: "assistant_clinic_manager", title: "Assistant Clinic Manager" },
  { id: "housekeeping", title: "Housekeeping" },
  { id: "area_manager", title: "Area Manager" },
];

const GROOMER_SUB_TYPES = [
  { id: "BATH", title: "Bath" },
  { id: "HAIRCUT", title: "Haircut" },
  { id: "CONTRACTUAL_BATH", title: "Contractual Bath" },
  { id: "CONTRACTUAL_HAIRCUT", title: "Contractual Haircut" },
];

const CONTRACTUAL = ["CONTRACTUAL_BATH", "CONTRACTUAL_HAIRCUT"];

const EMPLOYEE_ID_MIN_LENGTH = 4;
const EMPLOYEE_ID_MAX_LENGTH = 12;

const SERVER_FIELD_MAP: Record<string, string> = { cities: "city_ids" };

type FormValues = {
  name: string;
  phone_number: string;
  email: string;
  employee_id: string;
  staff_type: string;
  sub_type: string;
  city_ids: string[];
};

const Label = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <label className="text-xs font-medium text-[#1F1F23]">
    {children} {required && <span className="text-rose-500">*</span>}
  </label>
);

const FieldNote = ({ error, hint }: { error?: string; hint?: string }) => (
  <p className={`min-h-[16px] text-[11px] leading-4 ${error ? "text-rose-500" : "text-[#6C727F]"}`}>{error || hint}</p>
);

const StaffOnboardForm = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const [params] = useSearchParams();
  const editStaffId = params.get("staff_id") || "";

  const { forStaffName, forAlphaNumericWithoutSpace, forEmail, forMobile } = useFormValidations();

  const { execute: save, fieldErrors, isSaving } = useStaffProfileSaveAPI();
  const { execute: fetchDetail } = useStaffProfileDetailAPI();
  const { execute: fetchCities, cities } = useStaffProfileCitiesAPI();

  const [isActive, setIsActive] = useState(true);
  const [selectedCityIds, setSelectedCityIds] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ mode: "onChange", defaultValues: { staff_type: "", sub_type: "" } });

  const staffType = watch("staff_type");
  const subType = watch("sub_type");
  const isGroomer = staffType === "groomer";
  const isContractual = isGroomer && CONTRACTUAL.includes(subType);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  useEffect(() => {
    fieldErrors.forEach(({ field, message }) => {
      setError((SERVER_FIELD_MAP[field] || field) as keyof FormValues, { type: "manual", message });
    });
  }, [fieldErrors, setError]);

  useEffect(() => {
    if (!editStaffId) return;
    fetchDetail(editStaffId).then((staff) => {
      if (!staff) return;
      setValue("name", staff.name);
      setValue("phone_number", staff.phone_number);
      setValue("email", staff.email);
      setValue("employee_id", staff.employee_id);
      setValue("staff_type", staff.staff_type);
      setValue("sub_type", staff.sub_type || "");
      setIsActive(staff.status === "ACTIVE");
      const cityIds = (staff.cities || []).map((city) => city.city_id);
      setSelectedCityIds(cityIds);
      setValue("city_ids", cityIds);
    });
  }, [editStaffId, fetchDetail, setValue]);

  const cityItems = useMemo(
    () => cities.map((city) => ({ title: city.name, id: city.id, checkboxVisible: true, subSelectItems: [] })),
    [cities],
  );

  const selectedCityItems = useMemo(
    () => cityItems.filter((city) => selectedCityIds.includes(city.id)),
    [cityItems, selectedCityIds],
  );

  useEffect(() => {
    if (!isGroomer) setValue("sub_type", "");
  }, [isGroomer, setValue]);

  useEffect(() => {
    if (isContractual) setValue("employee_id", "");
  }, [isContractual, setValue]);

  const onSubmit = async (values: FormValues) => {
    const saved = await save({
      staff_id: editStaffId,
      name: values.name?.trim(),
      phone_number: values.phone_number,
      email: values.email?.trim(),
      employee_id: isContractual ? "" : values.employee_id?.trim(),
      staff_type: values.staff_type,
      sub_type: isGroomer ? values.sub_type : "",
      status: isActive ? "ACTIVE" : "INACTIVE",
      cities: cities
        .filter((city) => selectedCityIds.includes(city.id))
        .map((city) => ({ city_id: city.id, city_name: city.name })),
    });
    if (saved) navigate(getRouteKey("STAFF_ONBOARDING", "url"));
  };

  return (
    <div className="px-5 py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-base font-semibold text-slate-900">{editStaffId ? "Edit staff" : "Onboard staff"}</h1>
          <p className="mt-1 text-xs text-[#6C727F]">Clinics are assigned separately, from Staff Clinic Mapping.</p>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
          <span className="text-xs font-medium text-[#1F1F23]">{isActive ? "Active" : "Inactive"}</span>
          <Toggle checked={isActive} onToggle={() => setIsActive((prev) => !prev)} />
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-5">
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="px-5 py-5">
            <h2 className="text-sm font-semibold text-slate-900">Personal details</h2>
            <p className="mt-0.5 text-xs text-[#6C727F]">Used to identify this person across VMS.</p>

            <div className="mt-4 grid gap-x-6 gap-y-5 md:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <Label required>Name</Label>
                <Input
                  disableFieldError
                  errorMessages={[]}
                  className="text-xs"
                  inputClassName="text-sm"
                  placeholder="Full name"
                  register={register("name", {
                    required: "Name is required",
                    pattern: {
                      value: forStaffName.validations?.pattern as RegExp,
                      message: "Special characters are not allowed",
                    },
                  })}
                  fieldError={errors.name}
                />
                <FieldNote error={errors.name?.message} />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label required>Phone number</Label>
                <Input
                  disableFieldError
                  errorMessages={[]}
                  className="text-xs"
                  inputClassName="text-sm"
                  maxLength={10}
                  placeholder="10 digits"
                  register={register("phone_number", {
                    required: "Phone number is required",
                    pattern: {
                      value: forMobile.validations?.pattern as RegExp,
                      message: "Enter a valid 10 digit phone number",
                    },
                  })}
                  fieldError={errors.phone_number}
                />
                <FieldNote error={errors.phone_number?.message} />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label required>Email</Label>
                <Input
                  disableFieldError
                  errorMessages={[]}
                  className="text-xs"
                  inputClassName="text-sm"
                  placeholder="name@vetic.in"
                  register={register("email", {
                    required: "Email is required",
                    pattern: {
                      value: forEmail.validations?.pattern as RegExp,
                      message: "Enter a valid email address",
                    },
                  })}
                  fieldError={errors.email}
                />
                <FieldNote error={errors.email?.message} />
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 px-5 py-5">
            <h2 className="text-sm font-semibold text-slate-900">Role</h2>
            <p className="mt-0.5 text-xs text-[#6C727F]">
              Determines how this person appears in attendance and rota checks.
            </p>

            <div className="mt-4 grid gap-x-6 gap-y-5 md:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <Label required>Type of staff</Label>
                <NonSearchableSelectMenu
                  errorMessages={[{ message: "Staff type is required", type: "required" }]}
                  fieldError={errors.staff_type}
                  register={register("staff_type", { required: true })}
                  selectItems={STAFF_TYPES}
                  placeholder="Select type"
                  showTooltips={false}
                  showDropdownIcon
                  defaultSelected={STAFF_TYPES.find((type) => type.id === staffType)}
                  onSelectItem={(item) => item && setValue("staff_type", item.id, { shouldValidate: true })}
                  listBoxClassName="w-full"
                  containerClassName="w-full"
                />
              </div>

              {isGroomer && (
                <div className="flex flex-col gap-1.5">
                  <Label required>Groomer type</Label>
                  <NonSearchableSelectMenu
                    errorMessages={[{ message: "Groomer type is required", type: "required" }]}
                    fieldError={errors.sub_type}
                    register={register("sub_type", { required: true })}
                    selectItems={GROOMER_SUB_TYPES}
                    placeholder="Select groomer type"
                    showTooltips={false}
                    showDropdownIcon
                    defaultSelected={GROOMER_SUB_TYPES.find((type) => type.id === subType)}
                    onSelectItem={(item) => item && setValue("sub_type", item.id, { shouldValidate: true })}
                    listBoxClassName="w-full"
                    containerClassName="w-full"
                  />
                </div>
              )}

              {isContractual ? (
                <div className="flex flex-col gap-1.5">
                  <Label>Employee ID</Label>
                  <div className="flex h-[34px] items-center rounded-md border border-dashed border-slate-200 bg-slate-50 px-3">
                    <span className="text-xs text-[#6C727F]">Not applicable</span>
                  </div>
                  <FieldNote hint="Contractual groomers have no HR One code, so attendance is not matched." />
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <Label required>Employee ID</Label>
                  <Input
                    disableFieldError
                    errorMessages={[]}
                    className="text-xs"
                    inputClassName="text-sm"
                    maxLength={EMPLOYEE_ID_MAX_LENGTH}
                    placeholder="e.g. CSV1234"
                    register={register("employee_id", {
                      required: "Employee ID is required",
                      minLength: {
                        value: EMPLOYEE_ID_MIN_LENGTH,
                        message: `Employee ID must be at least ${EMPLOYEE_ID_MIN_LENGTH} characters`,
                      },
                      maxLength: {
                        value: EMPLOYEE_ID_MAX_LENGTH,
                        message: `Employee ID cannot exceed ${EMPLOYEE_ID_MAX_LENGTH} characters`,
                      },
                      pattern: {
                        value: forAlphaNumericWithoutSpace.validations?.pattern as RegExp,
                        message: "Only letters and numbers are allowed",
                      },
                    })}
                    fieldError={errors.employee_id}
                  />
                  <FieldNote
                    error={errors.employee_id?.message}
                    hint={`${EMPLOYEE_ID_MIN_LENGTH}\u2013${EMPLOYEE_ID_MAX_LENGTH} letters or numbers. Must match HR One exactly.`}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-slate-100 px-5 py-5">
            <h2 className="text-sm font-semibold text-slate-900">Cities</h2>
            <p className="mt-0.5 text-xs text-[#6C727F]">
              This person can later be mapped to clinics in these cities only.
            </p>

            <div className="mt-4 md:max-w-md">
              <SearchableMultiSelectMenu
                withSelectAll
                showTooltips
                withDropDownSearch
                errorMessages={[{ message: errors.city_ids?.message, type: "manual" }]}
                fieldError={errors.city_ids}
                register={register("city_ids")}
                showTypedErrors={false}
                placeholder="Select cities"
                defaultSelected={selectedCityItems}
                selectItems={cityItems}
                onSelectItem={(items) => {
                  const ids = Array.from(new Set((items || []).map((item) => item.id)));
                  setSelectedCityIds(ids);
                  setValue("city_ids", ids);
                }}
                listBoxClassName="w-full"
                containerClassName="w-full"
              />
            </div>

            {selectedCityIds.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedCityItems.map((city) => (
                  <span
                    key={city.id}
                    className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-800 ring-1 ring-slate-200"
                  >
                    {city.title}
                    <button
                      type="button"
                      aria-label={`Remove ${city.title}`}
                      className="text-slate-400 transition-colors hover:text-rose-500"
                      onClick={() => {
                        const ids = selectedCityIds.filter((id) => id !== city.id);
                        setSelectedCityIds(ids);
                        setValue("city_ids", ids);
                      }}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-3">
          <button
            type="button"
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#193A6A]/15"
            onClick={() => navigate(getRouteKey("STAFF_ONBOARDING", "url"))}
          >
            Cancel
          </button>
          <SolidButton size="2XL" asType="submit" disabled={isSaving}>
            {isSaving ? "Saving" : editStaffId ? "Save changes" : "Onboard staff"}
          </SolidButton>
        </div>
      </form>
    </div>
  );
};

export default StaffOnboardForm;
